import { useEffect, useRef, useCallback } from 'react';
import { conversationsAPI } from '../services/api';
import notificationService from '../services/notifications';
import { useRealtimeMessages } from './useRealtimeMessages';

// Hook for global notification polling (works on all pages)
export const useNotificationPolling = () => {
  const previousUnreadMessages = useRef(new Map());
  const isInitialLoad = useRef(true);

  const pollForNotifications = useCallback(async () => {
    try {
      // Only poll if notifications are enabled
      if (!notificationService.isEnabled()) {
        return;
      }

      // Fetch active (non-archived) conversations
      const response = await conversationsAPI.getAll({ showArchived: false });
      const conversations = response.data.conversations;

      // Skip notification checks on very first load
      if (isInitialLoad.current) {
        // Build initial state of unread messages
        conversations.forEach(conv => {
          if (conv.unread_count > 0 && conv.last_message_direction === 'inbound') {
            previousUnreadMessages.current.set(conv.id, {
              count: conv.unread_count,
              lastMessageId: conv.last_message_id,
              lastMessageText: conv.last_message_text,
              contactName: conv.contact_name || conv.contact_phone_number
            });
          }
        });
        isInitialLoad.current = false;
        return;
      }

      const newUnreadMessages = new Map();

      // Build map of current unread messages
      conversations.forEach(conv => {
        if (conv.unread_count > 0 && conv.last_message_direction === 'inbound') {
          newUnreadMessages.set(conv.id, {
            count: conv.unread_count,
            lastMessageId: conv.last_message_id,
            lastMessageText: conv.last_message_text,
            contactName: conv.contact_name || conv.contact_phone_number
          });
        }
      });

      // Detect new messages by comparing with previous state
      newUnreadMessages.forEach((current, convId) => {
        const previous = previousUnreadMessages.current.get(convId);

        // Show notification if:
        // 1. This is a new conversation with unread messages, OR
        // 2. The last message ID changed (new message arrived)
        if (!previous || previous.lastMessageId !== current.lastMessageId) {
          notificationService.showNewMessage(
            {
              conversation_id: convId,
              id: current.lastMessageId,
              message_text: current.lastMessageText,
              from_number: current.contactName
            },
            current.contactName
          );
        }
      });

      // Update previous state
      previousUnreadMessages.current = newUnreadMessages;
    } catch (error) {
      console.error('Notification polling error:', error);
      // Don't show errors to user for background polling
    }
  }, []);

  // Subscribe to Realtime for instant notifications (when enabled)
  const { isActive: realtimeActive } = useRealtimeMessages({
    onNewMessage: useCallback((message) => {
      // Instant notification via Realtime - refresh to get full conversation context
      if (message.direction === 'inbound') {
        pollForNotifications();
      }
    }, [pollForNotifications])
  });

  useEffect(() => {
    // Poll immediately on mount
    pollForNotifications();

    // When Realtime is active, reduce polling to 60s (fallback safety net)
    // Without Realtime, poll every 10s as before
    const pollInterval = realtimeActive ? 60000 : 10000;
    const interval = setInterval(pollForNotifications, pollInterval);

    return () => {
      clearInterval(interval);
    };
  }, [pollForNotifications, realtimeActive]);
};

export default useNotificationPolling;
